import { useEffect, useMemo, useRef, useState } from "react";
import { contactInput, type ContactDetail, type ContactFormValues, type ContactInput } from "@cx/contracts";

/** Um endereço do formulário: todos os campos opcionais, exceto kind (FE2). */
export type Addr = NonNullable<ContactFormValues["addresses"]>[number];
export type AddrPatch = Partial<Omit<Addr, "kind">>;

export const EMPTY: ContactFormValues = {
  name: "",
  personType: "J",
  document: "",
  ieIndicator: 9,
  status: "active",
  creditLimitMode: "unlimited",
  typeIds: [],
  addresses: [{ kind: "main", country: "BR" }],
  persons: [],
  customFields: {},
};

/** Converte o detalhe da API em valores do formulário, sem os campos que só o servidor tem. */
export function toFormValues(d: ContactDetail): ContactFormValues {
  const copy: Partial<ContactDetail> = { ...d };
  delete copy.id;
  delete copy.createdAt;
  delete copy.updatedAt;
  delete copy.types;
  return {
    ...EMPTY,
    ...copy,
    addresses: d.addresses ?? EMPTY.addresses,
    typeIds: d.typeIds ?? d.types.map((t) => t.id),
  };
}

const blank = (s: string | null | undefined) => (s ? s : null);

export function useContactForm(initial: ContactFormValues = EMPTY) {
  const [values, setValues] = useState<ContactFormValues>(initial);
  const [baseline, setBaseline] = useState<ContactFormValues>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const latest = useRef(values);
  useEffect(() => {
    latest.current = values;
  }, [values]);
  const dirty = useMemo(() => JSON.stringify(values) !== JSON.stringify(baseline), [values, baseline]);
  const set = <K extends keyof ContactFormValues>(k: K, v: ContactFormValues[K]) =>
    setValues((x) => ({ ...x, [k]: v }));
  const setAddr = (kind: Addr["kind"], patch: AddrPatch) =>
    setValues((x) => {
      const list = [...(x.addresses ?? [])];
      const i = list.findIndex((a) => a.kind === kind);
      if (i >= 0) list[i] = { ...list[i]!, ...patch };
      else list.push({ kind, country: "BR", ...patch });
      return { ...x, addresses: list };
    });
  /** Valida no cliente com o MESMO schema da API; devolve o payload limpo ou null. */
  const validate = (): ContactInput | null => {
    const clean = {
      ...values,
      document: blank(values.document),
      email: blank(values.email),
      nfeEmail: blank(values.nfeEmail),
      website: blank(values.website),
      fatherCpf: blank(values.fatherCpf),
      motherCpf: blank(values.motherCpf),
    };
    const r = contactInput.safeParse(clean);
    if (r.success) {
      setErrors({});
      return r.data;
    }
    const e: Record<string, string> = {};
    for (const i of r.error.issues) e[i.path.join(".")] ??= i.message;
    setErrors(e);
    return null;
  };
  /** Depois de salvar, os valores atuais viram a base do "alterado". */
  const markSaved = () => setBaseline(latest.current);
  return { values, set, setAddr, setValues, errors, setErrors, validate, dirty, markSaved };
}
